/* Reporting the wishlist onwards - to Klaviyo, and to Back in Stock.

   THE BROWSER IS STILL THE SOURCE OF TRUTH. The wishlist lives in localStorage
   and works with no network at all; everything here is an extra that runs
   after the fact and is allowed to fail. Nothing below can stop an item being
   saved, and nothing below is awaited by the interface.

   WHY A setItem HOOK rather than an event out of dico-wishlist.js. Saves come
   from three places - the heart on a product page, the delete in the drawer,
   the delete on the wishlist page - and all three agree on exactly one thing:
   they write the whole list to localStorage under one key. Hooking that write
   means one place to maintain instead of three call sites to keep in step, and
   a fourth caller added later is picked up for free. The patch is scoped to
   our key; every other setItem on the page passes straight through untouched.

   WHAT EACH SIDE IS WORTH.

   Klaviyo is a documented, supported integration and is the half to rely on.
   One caveat: onsite events attach to a profile Klaviyo already recognises in
   this browser, so a shopper who has never given an email is anonymous to it
   and their saves will not land against a profile.

   Back in Stock is NOT a documented API. These endpoints were found by
   watching the app's own widget - POST /apps/bis/wishlist.json opens a session
   and returns a uuid, POST and DELETE on /apps/bis/wishlist/items.json add and
   remove - so they can change without notice. Every call here fails quietly.

   AND WE SEND THE RIGHT SIZE. The app's own button is the reason this theme
   has its own wishlist at all: it resolves the variant internally and saves
   the wrong one. Observed on a product page sitting on EU 37, its button
   posted shopify_variant_id for EU 35. The variant is just a parameter on
   these endpoints, so sending it ourselves fixes what their widget got wrong.
*/
(function () {
    'use strict';

    var KEY = 'dico.wishlist.v1';

    /* The app's own key. Reused rather than invented so our items land in the
       same wishlist its widget would have used, not a second one beside it. */
    var BIS_UUID = 'amp.wishlist.uuid';

    function read(raw) {
        try {
            var list = raw ? JSON.parse(raw) : [];
            return Array.isArray(list) ? list : [];
        } catch (e) { return []; }
    }

    function byId(list) {
        var map = {};
        for (var i = 0; i < list.length; i++) { map[String(list[i].id)] = list[i]; }
        return map;
    }

    function handleOf(item) {
        if (item.handle) { return item.handle; }
        var m = String(item.url || '').match(/\/products\/([^/?#]+)/);
        return m ? m[1] : '';
    }

    /* One fetch per handle for the whole page life. The stored record carries a
       short title and a formatted price string, which is what the drawer needs
       and not what either service wants: Klaviyo wants a number it can sum and
       Back in Stock wants cents and a product id. Both come from here. */
    var cache = {};
    function product(handle) {
        if (!handle) { return Promise.resolve(null); }
        if (!cache[handle]) {
            cache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
                .then(function (r) { return r.ok ? r.json() : null; })
                .catch(function () { return null; });
        }
        return cache[handle];
    }

    function variantOf(prod, id) {
        var list = (prod && prod.variants) || [];
        for (var i = 0; i < list.length; i++) {
            if (String(list[i].id) === String(id)) { return list[i]; }
        }
        return null;
    }

    /* ---- Klaviyo ---------------------------------------------------------- */

    /* klaviyo.push is the current onsite queue; _learnq is the older name and
       still present on this storefront. Trying both means this keeps working
       whichever one the account is on, and doing nothing if neither exists. */
    function track(event, props) {
        try {
            if (window.klaviyo && typeof window.klaviyo.push === 'function') {
                window.klaviyo.push(['track', event, props]);
                return;
            }
            if (window._learnq && typeof window._learnq.push === 'function') {
                window._learnq.push(['track', event, props]);
            }
        } catch (e) {}
    }

    function klaviyoAdd(item, prod, v) {
        var price = v ? Number(v.price) / 100 : null;
        track('Added to Wishlist', {
            ProductName: (prod && prod.title) || item.title,
            ProductID: prod ? prod.id : null,
            VariantID: item.id,
            Size: item.size || (v && v.title) || '',
            Handle: handleOf(item),
            URL: window.location.origin + (item.url || ''),
            ImageURL: item.image || '',
            Available: v ? v.available !== false : null,
            $value: price
        });
    }

    function klaviyoRemove(item) {
        track('Removed from Wishlist', {
            ProductName: item.title,
            VariantID: item.id,
            Size: item.size || '',
            Handle: handleOf(item)
        });
    }

    /* ---- Back in Stock ----------------------------------------------------- */

    function uuid() {
        try { return window.localStorage.getItem(BIS_UUID); } catch (e) { return null; }
    }

    /* Opens a wishlist session if this browser has not got one. Resolves to
       null on any failure, and every caller treats null as "skip". */
    var session = null;
    function bisSession() {
        if (session) { return session; }
        var have = uuid();
        if (have) { session = Promise.resolve(have); return session; }

        session = fetch('/apps/bis/wishlist.json', { method: 'POST', credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (j) {
                if (j && j.uuid) {
                    try { window.localStorage.setItem(BIS_UUID, j.uuid); } catch (e) {}
                    return j.uuid;
                }
                return null;
            })
            .catch(function () { return null; });
        return session;
    }

    function qs(obj) {
        var out = [];
        for (var k in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== null && obj[k] !== undefined && obj[k] !== '') {
                out.push(encodeURIComponent(k) + '=' + encodeURIComponent(obj[k]));
            }
        }
        return out.join('&');
    }

    /* Parameters mirror the ones the app's own widget sends, so this is
       indistinguishable from its traffic apart from the variant being the one
       the customer actually chose. */
    function bisAdd(item, prod, v) {
        if (!prod || !v) { return; }
        bisSession().then(function (id) {
            if (!id) { return; }
            var url = '/apps/bis/wishlist/items.json?' + qs({
                uuid: id,
                shopify_product_id: prod.id,
                shopify_variant_id: v.id,
                handle: prod.handle,
                product_title: prod.title,
                variant_title: v.title,
                price: v.price,
                image_url: item.image || prod.featured_image || ''
            });
            fetch(url, { method: 'POST', credentials: 'same-origin' }).catch(function () {});
        });
    }

    function bisRemove(item) {
        var id = uuid();
        if (!id) { return; }
        var url = '/apps/bis/wishlist/items.json?' + qs({
            uuid: id,
            shopify_variant_id: item.id
        });
        fetch(url, { method: 'DELETE', credentials: 'same-origin' }).catch(function () {});
    }

    /* ---- The diff ---------------------------------------------------------- */

    /* Compared by variant id, deliberately. The wishlist page rewrites the whole
       list whenever it refreshes a stale price or a sold-out flag, which is a
       setItem with the same items in it. Diffing ids means those corrections
       are silent instead of firing a duplicate event per item per page view. */
    function sync(beforeRaw, afterRaw) {
        var before = byId(read(beforeRaw));
        var after = byId(read(afterRaw));
        var k;

        for (k in after) {
            if (Object.prototype.hasOwnProperty.call(after, k) && !before[k]) {
                (function (item) {
                    product(handleOf(item)).then(function (prod) {
                        var v = variantOf(prod, item.id);
                        klaviyoAdd(item, prod, v);
                        bisAdd(item, prod, v);
                    });
                }(after[k]));
            }
        }

        for (k in before) {
            if (Object.prototype.hasOwnProperty.call(before, k) && !after[k]) {
                klaviyoRemove(before[k]);
                bisRemove(before[k]);
            }
        }
    }

    /* ---- The hook ---------------------------------------------------------- */

    try {
        var original = Storage.prototype.setItem;

        Storage.prototype.setItem = function (key, value) {
            if (key !== KEY) { return original.apply(this, arguments); }

            var before = null;
            try { before = this.getItem(key); } catch (e) {}

            var result = original.apply(this, arguments);

            /* After the write and off the click's own stack, so a slow network
               or a thrown request can never delay the heart filling in. */
            window.setTimeout(function () {
                try { sync(before, value); } catch (e) {}
            }, 0);

            return result;
        };
    } catch (e) {}
}());
